import * as ko from 'knockout';

import { Deferred } from '../utils/deferred';
import { I18nText } from '../i18n_text';
import { app } from '../app';
import i18n from '../i18n';
import * as trialsApi from '../api/trials';
import { getContentRange } from '../api/request';
import { KEYCODE_ESCAPE } from '../utils';
import { getTrialStateLabel } from '../models/TrialState';

let template = require('raw-loader!../../templates/components/measurement_library_trials.html').default;

const PAGE_SIZE = 10;

interface TrialRow {
  nameJson: I18nText;
  state: string;
  href: string;
}

// One paginated section of the dialog ("Templates" or "Trials"). Pages through
// the trials list endpoint on demand instead of loading every row up front.
class TraitTrialsSection {
  rows = ko.observableArray<TrialRow>();
  loading = ko.observable(false);
  hasMore = ko.observable(false);
  private total: number | null = null;

  constructor(
    private nameSlug: string,
    private isTemplate: boolean
  ) {}

  loadMore = async () => {
    if (this.loading()) {
      return;
    }
    this.loading(true);
    try {
      const page = await trialsApi.list(
        {
          with_trait_slugs: [this.nameSlug],
          template: this.isTemplate,
          sort_by: 'name',
          sort_direction: 'asc',
          limit: PAGE_SIZE,
          offset: this.rows().length,
        },
        {
          injectTenant: true,
          onHeadersReceived: (headers) => {
            try {
              this.total = getContentRange(headers).total;
            } catch {
              this.total = null;
            }
          },
        }
      );
      this.rows.push(
        ...page.map((row) => ({
          nameJson: row.name_json,
          state: getTrialStateLabel(row.state),
          href: `/trials/${row.id}/`,
        }))
      );
      this.hasMore(
        this.total === null ? page.length === PAGE_SIZE : this.rows().length < this.total
      );
    } finally {
      this.loading(false);
    }
  };
}

class MeasurementLibraryTrials {
  loading = ko.observable(true);
  templates: TraitTrialsSection;
  trials: TraitTrialsSection;
  isEmpty = ko.pureComputed(
    () => !this.loading() && this.templates.rows().length === 0 && this.trials.rows().length === 0
  );
  result: Deferred<{}>;

  constructor(params: { nameSlug: string; result: Deferred<{}> }) {
    this.result = params.result;
    this.templates = new TraitTrialsSection(params.nameSlug, true);
    this.trials = new TraitTrialsSection(params.nameSlug, false);
    this.loadInitial();

    document.addEventListener('keyup', this.onKeyUp);
  }

  dispose() {
    document.removeEventListener('keyup', this.onKeyUp);
  }

  private async loadInitial() {
    try {
      await Promise.all([this.templates.loadMore(), this.trials.loadMore()]);
    } finally {
      this.loading(false);
    }
  }

  onKeyUp = (event: KeyboardEvent) => {
    if (event.which === KEYCODE_ESCAPE || event.keyCode === KEYCODE_ESCAPE) {
      this.onClose();
    }
  };

  onClose = () => {
    this.result.resolve(null);
  };
}

const name = 'measurement-library-trials';

ko.components.register(name, {
  viewModel: MeasurementLibraryTrials,
  template: template,
});

export function openMeasurementLibraryTrials(nameSlug: string) {
  app.formsStackController.push({
    title: i18n.t('Templates and trials using this trait')(),
    name,
    isBig: true,
    params: { nameSlug, result: new Deferred<{}>() },
  });
}
